import {
	Checkbox,
	Form,
	FormItem,
	ValidationError
} from "client/Components";
import {
	AddTilesResponseModel,
	IntegrationAppDetailsTileModel,
	IntegrationUpdateTileModel,
	updateIntegrationTile
} from "client/Api/Workspace/workspace";
import { IFormHandler, useFormHandler } from "client/Forms";
import { createValidationConfiguration, IValidationConfiguration } from "client/Validation";
import {
	isNotNullNorUndefined,
	isNotEmpty,
	doesNotExceedLength
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useImperativeHandle,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { ExistingTile } from "./common";
import { EditTileFromData, initialGenericState } from "./EditGenericTile";
import { EditTileHandle } from "./EditTile";
import { EditTileGeneralFields } from "./GeneralFields";
import { useUpdateTile, UpdateTileFormData } from "./hooks/useUpdateTile";
import { isNotNil } from "client/Misc/utils";

export type EditIntegrationTileProps = {
	initialModel: IntegrationAppDetailsTileModel;
	mode: ExistingTile;
	isLoading: boolean;
	onIsLoadingChange: (value: boolean) => void;
	onAfterSave: (response?: AddTilesResponseModel) => void;
};

export const EditIntegrationTile = forwardRef(
	(
		props: EditIntegrationTileProps,
		ref: Ref<EditTileHandle>
	): ReactElement => {
		const {
			initialModel,
			mode,
			isLoading,
			onIsLoadingChange,
			onAfterSave
		} = props;

		const [formData, setFormData] = useState<IntegrationTileEditFormData>({
			...initialGenericState(initialModel),
			hideHeader: initialModel.hideHeader,
			hideFooter: initialModel.hideFooter
		});

		const formHandler: IFormHandler<IntegrationTileEditFormData> = useFormHandler(
			formData,
			setFormData,
			validationBuilder
		);

		const shouldShowHideFooter: boolean = isNotNil(initialModel.navigationUrl);

		const { updateHandler } = useUpdateTile<IntegrationUpdateTileModel>(
			formHandler,
			updateIntegrationTile,
			isLoading,
			onIsLoadingChange,
			mode,
			onAfterSave,
			(formData) => prepareForSave(formData, shouldShowHideFooter)
		);

		useImperativeHandle(
			ref,
			(): EditTileHandle => ({
				updateHandler
			}),
			[updateHandler]
		);

		return (
			<Form
				onSubmit="NONE"
			>
				<EditTileGeneralFields
					initialModel={initialModel}
					formHandler={formHandler}
				/>

				<FormItem>
					<Checkbox
						{...formHandler.for("hideHeader").attachToCheckbox()}
						text={localize("_HideTileHeader")}
					/>
					<ValidationError
						{...formHandler.for("hideHeader").attachToValidationError()}
					/>
				</FormItem>
				{shouldShowHideFooter && (
					<FormItem>
						<Checkbox
							{...formHandler.for("hideFooter").attachToCheckbox()}
							text={localize("_HideTileFooter")}
						/>
						<ValidationError
							{...formHandler.for("hideFooter").attachToValidationError()}
						/>
					</FormItem>
				)}
			</Form>
		);
	}
);
EditIntegrationTile.displayName = "EditIntegrationTile";

const validationBuilder: IValidationConfiguration<IntegrationTileEditFormData> = createValidationConfiguration<IntegrationTileEditFormData>();

validationBuilder.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

type IntegrationTileEditFormData = EditTileFromData<
	Omit<
		IntegrationUpdateTileModel,
		"hideFooter"
	> & {
		hideFooter: boolean;
	}
>;

function prepareForSave(
	formData: UpdateTileFormData<IntegrationUpdateTileModel>,
	shouldIncludeHideFooterSetting: boolean
): UpdateTileFormData<IntegrationUpdateTileModel> {
	return {
		...formData,
		hideFooter: shouldIncludeHideFooterSetting
			? formData.hideFooter
			: null
	};
}

